import { Icon } from "@iconify/react";
import { Container, LandingButton, SIGN_UP_PATH } from "./primitives";

// The tilted 3D dashboard, with feathered edges so it blends into the
// section background at any width. Never shown larger than its 562px, so it stays sharp.
const HERO_IMAGE = "/hero-dashboard-v2.png";

const HERO_NOTES = ["No credit card required", "HIPAA-compliant", "Free for individuals"];

const TRUST_ITEMS = [
  { icon: "lucide:shield",   title: "HIPAA Compliant",             sub: "Fully certified"                 },
  { icon: "lucide:lock",     title: "Encrypted End-to-End",        sub: "AES-256 at rest & in transit"    },
  { icon: "lucide:cpu",      title: "AI-Assisted, Human-Verified", sub: "Every record reviewed"           },
  { icon: "lucide:eye",      title: "Your Data Is Never Sold",     sub: "No third-party sharing, ever"    },
];

export function HeroSection() {
  return (
    <section className="overflow-x-clip bg-[linear-gradient(180deg,#FEF8F9_0%,#FFFFFF_70%)]">
      <Container
        size="narrow"
        className="flex flex-col items-center gap-12 pt-[52px] pb-16 lg:flex-row lg:items-center lg:justify-between lg:gap-8 lg:pb-20"
      >
        {/* Copy */}
        <div className="w-full max-w-[560px] lg:w-[480px] lg:max-w-none lg:shrink-0">
          <h1 className="!mt-0 text-[44px] font-extrabold leading-[1.04] tracking-[-0.03em] text-[#120240] md:text-[56px] lg:text-[60px]">
            Every hospital
            <br />
            record.
            <br />
            Every format.
            <br />
            <span className="text-[#F18095]">One place</span>,
            <br />
            automatically.
          </h1>

          <p className="!mt-6 max-w-[480px] text-[18px] leading-[30px] text-[#4B5563]">
            Records come in however they exist: upload, scan, or connect your hospital. MedSyncIQ reads them,
            verifies them, and surfaces what matters.
          </p>

          <div className="mt-9 flex flex-wrap gap-3.5">
            <LandingButton to={SIGN_UP_PATH} className="!h-[52px] !px-6 !text-[16px] !font-semibold">
              Get Started Free
            </LandingButton>
            <LandingButton variant="outline" arrow className="!h-[52px] !px-6 !text-[16px] !font-semibold" href="#how-it-works">
              See How It Works
            </LandingButton>
          </div>

          <ul className="!mt-[22px] flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[13px] text-[#6B7280]">
            {HERO_NOTES.map((note, i) => (
              <li key={note} className="flex items-center gap-2.5">
                {i > 0 && <span className="h-[3px] w-[3px] rounded-full bg-[#D1D5DB]" />}
                <span className="flex items-center gap-1.5">
                  <Icon icon="lucide:check" className="h-3 w-3 text-[#9CA3AF]" />
                  {note}
                </span>
              </li>
            ))}
          </ul>
        </div>

        {/* Illustration: centred beside the copy, right-aligned on desktop. */}
        <div className="flex w-full justify-center lg:flex-1 lg:justify-end">
          <img
            src={HERO_IMAGE}
            alt="MedSyncIQ patient dashboard: records, lab results, visits, medications and health timeline"
            width={562}
            height={462}
            className="h-auto w-full max-w-[562px] select-none"
            draggable={false}
          />
        </div>
      </Container>

      {/* Trust bar */}
      <div className="bg-[#F18095]">
        <Container className="grid grid-cols-2 gap-x-6 gap-y-5 py-6 lg:grid-cols-4 lg:py-0">
          {TRUST_ITEMS.map(item => (
            <div key={item.title} className="flex items-center gap-3 lg:h-[94px]">
              <span className="flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-lg bg-white">
                <Icon icon={item.icon} className="h-[18px] w-[18px] text-[#F18095]" />
              </span>
              <span>
                <span className="block text-[14px] font-bold leading-5 text-white">{item.title}</span>
                <span className="block text-[12px] leading-4 text-white/70">{item.sub}</span>
              </span>
            </div>
          ))}
        </Container>
      </div>
    </section>
  );
}
